<div class="rl-page lm-page">

  <!-- ── Page Header ──────────────────────────────────── -->
  <div class="lm-page-header">
    <button class="lm-back-btn" (click)="navigateBack()" [title]="'leave_management.request_leave_page.title.back' | transloco">
      <mat-icon>{{ 'leave_management.request_leave_page.arrow_back' | transloco }}</mat-icon>
    </button>
    <span class="lm-page-title">{{ 'leave_management.request_leave_page.request_leave' | transloco }}</span>
  </div>

  <lm-loading-overlay [show]="isLoading" message="Loading..."></lm-loading-overlay>
  <lm-loading-overlay [show]="isSubmitting" message="Submitting..."></lm-loading-overlay>

  <!-- ── Main Card ───────────────────────────────────── -->
  <div class="lm-card rl-card">
    <div class="rl-layout">

      <!-- ════════════════ LEFT SECTION ════════════════ -->
      <div class="rl-left">

        <!-- Left Tabs -->
        <div class="rl-left-tabs">
          <button class="rl-tab-btn" [class.active]="leftTab === 0" (click)="switchLeftTab(0)">
            {{ 'leave_management.request_leave_page.leave_request' | transloco }}
          </button>
          <button class="rl-tab-btn" [class.active]="leftTab === 1" (click)="switchLeftTab(1)">
            {{ 'leave_management.request_leave_page.comp_off_request' | transloco }}
          </button>
        </div>

        <!-- Balance Banner -->
        <div class="balance-banner">
          @if (leftTab === 0) {Total {{ selectedLeaveTypeName }} Leave Balance: {{ selectedLeaveBalance }}}
          @if (leftTab === 1) {Total Compoff Balance: {{ compOffBalance }}}
        </div>

        <!-- ═══ LEAVE REQUEST FORM ═══ -->
        @if (leftTab === 0) {
        <form [formGroup]="leaveForm" class="rl-form">

          <!-- Submit Request To: single supervisor → read-only; multiple → dropdown -->
          <div class="lm-form-group">
            <label class="lm-field-label">Submit Request to <span class="lm-req">*</span></label>

            @if (managers.length <= 1) {
              <!-- Single supervisor: auto-selected, read-only -->
              @if (selectedLeaveManager) {
              <div class="lm-chip-row">
                <span class="lm-chip lm-chip--readonly">{{ selectedLeaveManager.name }}</span>
              </div>
              }
              @if (managers.length === 0) {
              <div class="lm-field-hint">{{ 'leave_management.request_leave_page.no_supervisors_configured' | transloco }}</div>
              }
            } @else {
              <!-- Multiple supervisors: single-select dropdown + chip -->
              <lib-ers-searchable-select
                formControlName="requestToId"
                [options]="managers"
                optionValue="id"
                optionLabel="name"
                emptyOptionLabel="{{ 'leave_management.request_leave_page.select' | transloco }}"
                (selectionChange)="onLeaveManagerChange($event.value)"
              ></lib-ers-searchable-select>
              @if (selectedLeaveManager) {
              <div class="lm-chip-row">
                <span class="lm-chip">
                  {{ selectedLeaveManager.name }}
                  <button class="lm-chip-x" (click)="removeLeaveManager()" type="button">×</button>
                </span>
              </div>
              }
            }
          </div>

          <div class="lm-form-group">
            <label class="lm-field-label">Leave Type <span class="lm-req">*</span></label>
            <mat-form-field appearance="outline" class="lm-full-width">
              <input matInput
                     #leaveTypeInput
                     [formControl]="leaveTypeSearchCtrl"
                     [matAutocomplete]="leaveTypeAuto"
                     [placeholder]="'leave_management.request_leave_page.placeholder.search_or_select_leave_type' | transloco"
                     autocomplete="off">
              <button mat-icon-button matSuffix type="button"
                      (click)="toggleLeaveTypePanel($event)"
                      tabindex="-1"
                      [disabled]="leaveTypeSearchCtrl.disabled">
                <mat-icon>{{ 'leave_management.request_leave_page.arrow_drop_down' | transloco }}</mat-icon>
              </button>
              <mat-autocomplete #leaveTypeAuto="matAutocomplete"
                                (optionSelected)="onLeaveTypeSelected($event)">
                @for (type of filteredLeaveTypes; track type.id) {
                <mat-option [value]="type.id">
                  {{ type.name }}
                </mat-option>
                }
                @if (filteredLeaveTypes.length === 0) {
                <mat-option disabled>
                  {{ 'leave_management.request_leave_page.no_results' | transloco }}
                </mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
            <!-- Policy document links -->
            @for (doc of policyLinks; track $index) {
              <a class="lr-policy-link" (click)="openPolicyDoc(doc.url)">
                <mat-icon class="lr-policy-icon">{{ 'leave_management.request_leave_page.description' | transloco }}</mat-icon>
                {{ doc.name }}
              </a>
            }
          </div>
          <div class="lm-form-group">
            <label class="lm-field-label">Start Date <span class="lm-req">*</span></label>
            <mat-form-field appearance="outline" class="lm-full-width">
              <input matInput [matDatepicker]="startPicker" formControlName="startDate"
                     [max]="leaveForm.get('endDate')?.value"
                     readonly placeholder="MM/DD/YYYY">
              <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
              <mat-datepicker #startPicker></mat-datepicker>
            </mat-form-field>
          </div>

          <!-- End Date: min = start date value -->
          <div class="lm-form-group">
            <label class="lm-field-label">End Date <span class="lm-req">*</span></label>
            <mat-form-field appearance="outline" class="lm-full-width">
              <input matInput [matDatepicker]="endPicker" formControlName="endDate"
                     [min]="leaveForm.get('startDate')?.value"
                     readonly placeholder="MM/DD/YYYY">
              <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
              <mat-datepicker #endPicker></mat-datepicker>
            </mat-form-field>
          </div>

          <!-- Description: optional, max 2000 chars -->
          <div class="lm-form-group">
            <label class="lm-field-label">{{ 'leave_management.request_leave_page.description_55f8eb' | transloco }}</label>
            <mat-form-field appearance="outline" class="lm-full-width">
              <textarea matInput formControlName="description" rows="3"
                        maxlength="2000"
                        [placeholder]="'leave_management.request_leave_page.placeholder.optional_description' | transloco"></textarea>
            </mat-form-field>
            <div class="lm-char-counter">{{ descriptionLength }} / 2000</div>
          </div>

        </form>
        }

        <!-- ═══ COMP OFF REQUEST FORM ═══ -->
        @if (leftTab === 1) {
        <form [formGroup]="compOffForm" class="rl-form">

          <div class="lm-form-group">
            <label class="lm-field-label">Requested No Of Days <span class="lm-req">*</span></label>
            <mat-form-field appearance="outline" class="lm-full-width">
              <input matInput type="number" formControlName="requestedDays" min="1">
            </mat-form-field>
          </div>

          <div class="lm-form-group">
            <label class="lm-field-label">Choose Comp Off Dates <span class="lm-req">*</span></label>
            <mat-form-field appearance="outline" class="lm-full-width">
              <input matInput [matDatepicker]="compOffPicker" [formControl]="compOffDateCtrl"
                     readonly placeholder="MM/DD/YYYY">
              <mat-datepicker-toggle matSuffix [for]="compOffPicker"></mat-datepicker-toggle>
              <mat-datepicker #compOffPicker (closed)="onCompOffDateClosed()"></mat-datepicker>
            </mat-form-field>
            @if (selectedCompOffDates.length > 0) {
            <div class="lm-chip-row">
              @for (d of selectedCompOffDates; track $index) {
              <span class="lm-chip">
                {{ fmtMDY(d) }}
                <button class="lm-chip-x" (click)="removeCompOffDate(d)" type="button">×</button>
              </span>
              }
            </div>
            }
          </div>

          <!-- Swap Icon -->
          <div class="swap-row">
            <button class="swap-btn" type="button" (click)="swapDates()" [title]="'leave_management.request_leave_page.title.interchange_dates' | transloco"
                    [disabled]="selectedCompOffDates.length === 0 || selectedCompOffDates.length !== selectedWorkedDates.length">
              <mat-icon>{{ 'leave_management.request_leave_page.swap_vert' | transloco }}</mat-icon>
            </button>
          </div>

          <!-- Choose Worked Dates: date picker → chip list (same as comp off dates) -->
          <div class="lm-form-group">
            <label class="lm-field-label">Choose Worked Dates <span class="lm-req">*</span></label>
            <mat-form-field appearance="outline" class="lm-full-width">
              <input matInput [matDatepicker]="workedPicker" [formControl]="workedDateCtrl"
                     readonly placeholder="MM/DD/YYYY">
              <mat-datepicker-toggle matSuffix [for]="workedPicker"></mat-datepicker-toggle>
              <mat-datepicker #workedPicker (closed)="onWorkedDateClosed()"></mat-datepicker>
            </mat-form-field>
            @if (selectedWorkedDates.length > 0) {
            <div class="lm-chip-row">
              @for (d of selectedWorkedDates; track $index) {
              <span class="lm-chip">
                {{ fmtMDY(d) }}
                <button class="lm-chip-x" (click)="removeWorkedDate(d)" type="button">×</button>
              </span>
              }
            </div>
            }
          </div>

          <!-- Submit Request To: single supervisor → auto-selected label; multiple → dropdown -->
          <div class="lm-form-group">
            <label class="lm-field-label">Submit Request to <span class="lm-req">*</span></label>

            @if (managers.length <= 1) {
              <!-- Single supervisor: show as read-only chip (auto-selected) -->
              @if (selectedCompOffManagers.length > 0) {
              <div class="lm-chip-row">
                <span class="lm-chip lm-chip--readonly">{{ selectedCompOffManagers[0].name }}</span>
              </div>
              }
              @if (managers.length === 0) {
              <div class="lm-field-hint">{{ 'leave_management.request_leave_page.no_supervisors_configured' | transloco }}</div>
              }
            } @else {
              <!-- Multiple supervisors: multi-select dropdown + chips -->
              <lib-ers-searchable-select
                formControlName="requestToIds"
                [options]="managers"
                optionValue="id"
                optionLabel="name"
                (selectionChange)="onCompOffManagersChange($event.value)"
              ></lib-ers-searchable-select>
              @if (selectedCompOffManagers.length > 0) {
              <div class="lm-chip-row">
                @for (mgr of selectedCompOffManagers; track mgr.id) {
                <span class="lm-chip">
                  {{ mgr.name }}
                  <button class="lm-chip-x" (click)="removeCompOffManager(mgr.id)" type="button">×</button>
                </span>
                }
              </div>
              }
            }
          </div>

        </form>
        }

        <!-- Footer Buttons -->
        <div class="rl-form-footer">
          <button class="lm-btn lm-btn--reset" type="button" (click)="onReset()">{{ 'leave_management.request_leave_page.reset' | transloco }}</button>
          <button class="lm-btn lm-btn--success" type="button" (click)="onSubmit()" [disabled]="isSubmitting">
            @if (isSubmitting) {
            <span class="lm-btn-spinner"></span>
            }
            {{ isSubmitting ? 'Submitting...' : 'Submit Request' }}
          </button>
        </div>

      </div>

      <!-- ════════════════ RIGHT SECTION ════════════════ -->
      <ers-leave-holiday-panel
        [activeTab]="rightTab"
        [companyHolidayRows]="companyHolidayRows"
        [optionalHolidayRows]="optionalHolidayRows"
        [pendingWorkedRows]="pendingWorkedRows"
        [holidayColDefs]="holidayColDefs"
        [workedDateColDefs]="workedDateColDefs"
        [takenCompOffs]="takenCompOffs"
        [showTakenCompOffCard]="true">
      </ers-leave-holiday-panel>
    </div>
  </div>
</div>